import { Box, Flex, Text } from '@byecode/ui'
import { NavigationShowMode } from '@lighthouse/core'
import React, { useMemo } from 'react'

import { RoundAvatar } from '@/components/RoundAvatar'

import * as SC from './styles'
import type { PlatformProps } from './types'

export const Mobile: React.FC<PlatformProps> = ({
    navBackgroundColor,
    iconColor,
    appName,
    appIcon,
    appDomainPrefix,
    activeBandColor,
    iconBackground,
    color
}) => {
    const layout = NavigationShowMode.horizontal
    const title = useMemo(() => {
        if (appName) {
            return (
                <Text color={color} style={{ flex: 1 }} size={20}>
                    {appName}
                </Text>
            )
        }
        return (
            <SC.AppNav>
                <SC.CrossBand width={88} height={20} borderRadius={8} background={activeBandColor} />
            </SC.AppNav>
        )
    }, [activeBandColor, appName, color])

    const content = useMemo(() => {
        return (
            <SC.Content isHorizontal={layout === NavigationShowMode.horizontal} background={navBackgroundColor}>
                <SC.AppMobileHeader alignItems="center">
                    <SC.AppInfo isHorizontal={layout === NavigationShowMode.horizontal}>
                        <RoundAvatar
                            iconSize={24}
                            size={32}
                            type="icon"
                            radius="8px"
                            bordered={false}
                            iconColor={iconColor}
                            icon={appIcon}
                            background={iconBackground}
                        />
                        {title}
                    </SC.AppInfo>
                </SC.AppMobileHeader>
            </SC.Content>
        )
    }, [appIcon, iconBackground, iconColor, layout, navBackgroundColor, title])

    return (
        <SC.Mobile>
            <SC.ToolBar gap={8} height={66} pl={12} pr={12} alignItems="center">
                <Flex gap={20} alignItems="center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M2.89289 11.2929C2.50237 11.6834 2.50237 12.3166 2.89289 12.7071C3.28342 13.0976 3.91658 13.0976 4.30711 12.7071L2.89289 11.2929ZM12 3.60001L12.7071 2.8929C12.3166 2.50238 11.6834 2.50238 11.2929 2.8929L12 3.60001ZM19.6929 12.7071C20.0834 13.0976 20.7166 13.0976 21.1071 12.7071C21.4976 12.3166 21.4976 11.6834 21.1071 11.2929L19.6929 12.7071ZM17 9.60001V18H19V9.60001H17ZM15.6 19.4H8.4V21.4H15.6V19.4ZM7 18V9.60001H5V18H7ZM8.4 19.4C7.6268 19.4 7 18.7732 7 18H5C5 19.8778 6.52223 21.4 8.4 21.4V19.4ZM17 18C17 18.7732 16.3732 19.4 15.6 19.4V21.4C17.4778 21.4 19 19.8778 19 18H17ZM4.30711 12.7071L12.7071 4.30711L11.2929 2.8929L2.89289 11.2929L4.30711 12.7071ZM11.2929 4.30711L19.6929 12.7071L21.1071 11.2929L12.7071 2.8929L11.2929 4.30711Z"
                            fill="#333333"
                        />
                    </svg>
                </Flex>
                <SC.MobileAddress gap={8} alignItems="center">
                    <svg width="9" height="13" viewBox="0 0 9 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M4.5 1.75C5.32843 1.75 6 2.42157 6 3.25V4.75H3V3.25C3 2.42157 3.67157 1.75 4.5 1.75ZM2 4.75V3.25C2 1.86929 3.11929 0.75 4.5 0.75C5.88071 0.75 7 1.86929 7 3.25V4.75H8C8.55229 4.75 9 5.19772 9 5.75V11.25C9 11.8023 8.55229 12.25 8 12.25H1C0.447715 12.25 0 11.8023 0 11.25V5.75C0 5.19772 0.447715 4.75 1 4.75H2Z"
                            fill="#5F6368"
                        />
                    </svg>

                    <SC.Url>{appDomainPrefix ? `${appDomainPrefix}.byecode.site` : 'sample.byecode.site'}</SC.Url>
                </SC.MobileAddress>
                <Flex gap={6} alignItems="center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_87832_767768)">
                            <rect x="4.75" y="4.75" width="14.5" height="14.5" rx="4.25" stroke="#333333" strokeWidth="1.5" />
                            <path d="M13.0449 7.87598V15H11.6387V9.49707L9.94922 10.0342V8.92578L12.8936 7.87598H13.0449Z" fill="#333333" />
                        </g>
                        <defs>
                            <clipPath id="clip0_87832_767768">
                                <rect width="16" height="16" fill="white" transform="translate(4 4)" />
                            </clipPath>
                        </defs>
                    </svg>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M10.5938 16.5938C11 16.1875 11.4688 15.9844 12 15.9844C12.5312 15.9844 13 16.1875 13.4062 16.5938C13.8125 17 14.0156 17.4688 14.0156 18C14.0156 18.5312 13.8125 19 13.4062 19.4062C13 19.8125 12.5312 20.0156 12 20.0156C11.4688 20.0156 11 19.8125 10.5938 19.4062C10.1875 19 9.98438 18.5312 9.98438 18C9.98438 17.4688 10.1875 17 10.5938 16.5938ZM10.5938 10.5938C11 10.1875 11.4688 9.98438 12 9.98438C12.5312 9.98438 13 10.1875 13.4062 10.5938C13.8125 11 14.0156 11.4688 14.0156 12C14.0156 12.5312 13.8125 13 13.4062 13.4062C13 13.8125 12.5312 14.0156 12 14.0156C11.4688 14.0156 11 13.8125 10.5938 13.4062C10.1875 13 9.98438 12.5312 9.98438 12C9.98438 11.4688 10.1875 11 10.5938 10.5938ZM13.4062 7.40625C13 7.8125 12.5312 8.01562 12 8.01562C11.4688 8.01562 11 7.8125 10.5938 7.40625C10.1875 7 9.98438 6.53125 9.98438 6C9.98438 5.46875 10.1875 5 10.5938 4.59375C11 4.1875 11.4688 3.98438 12 3.98438C12.5312 3.98438 13 4.1875 13.4062 4.59375C13.8125 5 14.0156 5.46875 14.0156 6C14.0156 6.53125 13.8125 7 13.4062 7.40625Z"
                            fill="#333333"
                        />
                    </svg>
                </Flex>
            </SC.ToolBar>
            {content}
            <SC.AppMobileContent>
                {Array.from({ length: 8 }).map((_, index) => (
                    <SC.Box key={index} />
                ))}
            </SC.AppMobileContent>
        </SC.Mobile>
    )
}
